import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useHistory, Redirect } from 'react-router-dom'
import { camelCase } from 'lodash'
import { Helmet } from 'react-helmet'

import {
    FeatureList,
    Column,
    ColumnOptions,
    PriceBox,
    PriceSwitcher,
    Table,
    LetsTalk,
} from '../../components/PriceTable'

import QAChat from '../../components/QAChat'
import Loader from '../../components/Loader'

import DataBox from './components/DataBox/DataBox'

import { API__PRODUCTS } from '../../api'
import { PRODUCTS__SET } from '../../store/actions'

const Pricing = ({ t, isEducation }) => {
    const products = useSelector(state => state.products)
    const dispatch = useDispatch()

    useEffect(() => {
        const fetch = async () => {
            isEducation
                ? dispatch(PRODUCTS__SET(await API__PRODUCTS.GET_PAYMENT_PRODUCTS_EDUCATION()))
                : dispatch(PRODUCTS__SET(await API__PRODUCTS.GET_PAYMENT_PRODUCTS()))
        }

        fetch()
    }, [isEducation])

    const QA_DATA = isEducation
        ? [
              ['question', t('What happens if the student visit limit is exceeded?')],
              [
                  'answer',
                  t(
                      'Do not worry! If you exceed the limit, we will send you an \nemail. You will have enough time to upgrade your tariff if \nnecessary. Your projects are safe.',
                  ),
              ],
              ['question', t('Will be my projects deleted after some time?')],
              ['answer', t('No, projects are not deleted. Your work is in safe.')],
          ]
        : [
              ['question', t('What happens when the pageviews limit is exceeded?')],
              [
                  'answer',
                  t(
                      'When the pageviews limit is exceeded, additional advertising \nmay be enabled. The project is not blocked.',
                  ),
              ],
              ['question', t('Are there limits on the number of published projects per month?')],
              ['answer', t('No, you can create an unlimited number of quizzes \nor other projects.')],
              ['question', t('Will be my projects deleted after some time?')],
              ['answer', t('No, projects are not deleted. Your work is in safe.')],
          ]

    const isMobile = useMediaQuery({ query: '(max-width: 991px)' })

    const [priceType, setPriceType] = useState('annual')

    const history = useHistory()

    const getAction = priceName => () =>
        history.push({
            pathname: '/auth',
            state: {
                to: {
                    pathname: 'account/billing-and-payments',
                    search: '?tab=subscription',
                    state: { payload: { priceType, priceName } },
                },
            },
        })

    if (!products) {
        return <Loader />
    }

    const reduceAnnualYouSaveMsg = tariff => {
        const annualPrice = products['annual'].filter(({ name }) => name === tariff)[0].price
        const monthPrice = products['month'].filter(({ name }) => name === tariff)[0].price

        const save = Math.round(monthPrice * 12 - annualPrice)

        return `${t('_pages._billing_and_payments.you_save')} $${save}`
    }

    const lang = i18next.language

    const priceHeader = isEducation
        ? t('Start for free, \nUpgrade at any time')
        : t('Upgrade, Downgrade \nor cancel at any time')

    return (
        <div className="pricing-container">
            <Helmet>
                <title>{t('Pricing')} | Interacty</title>
            </Helmet>

            <div className="pricing">
                <h1>{isEducation ? t('Educational plans') : t('Ready to rock? Choose your plan')}</h1>

                {!isEducation && (
                    <DataBox
                        title={t('Free for up to 28 days')}
                        text={t(
                            'Activate your 14-day trial and start creating awesome interactive content. \nNeed more time to make a decision? Extend your trial period for another 14 days!',
                        )}
                        linkText={t('Start Free Trial')}
                        linkUrl={'/auth'}
                    />
                )}

                <div className="pricing__price">
                    {!isMobile && <PriceSwitcher defaultChecked={priceType} onSwitch={setPriceType} />}
                    <Table className="pricing__table">
                        <div className="pricing__feature-list">
                            {isMobile ? (
                                <PriceSwitcher defaultChecked={priceType} onSwitch={setPriceType} />
                            ) : (
                                <span className="pricing__price-header">{priceHeader}</span>
                            )}
                            {isMobile ? null : (
                                <FeatureList
                                    list={Object.fromEntries(
                                        products[priceType][0].featureProducts.map(item => [
                                            camelCase(item.name),
                                            { name: item.name, description: item.under },
                                        ]),
                                    )}
                                    style={{
                                        height: `${products[priceType][0].featureProducts.length * 68 + 119}px `,
                                    }}
                                />
                            )}
                        </div>
                        {products[priceType].map((description, i) => {
                            const priceDescription =
                                description.billingCycleValue === 12 && description.name !== 'Free'
                                    ? reduceAnnualYouSaveMsg(description.name)
                                    : ''

                            const priceMap = price =>
                                description.billingCycleValue === 12 ? Math.round(price / 12) : price

                            return (
                                <Column key={i} className={`pricing__column-table`} description={description}>
                                    {(description, options, isMobile) => {
                                        return (
                                            <>
                                                {description.name !== 'Enterprise' ? (
                                                    <>
                                                        <PriceBox
                                                            title={description.name}
                                                            price={priceMap(description.price)}
                                                            style={{ marginTop: '32px' }}
                                                        />
                                                        <span className="pricing__price-description">
                                                            {priceDescription ? priceDescription : ''}
                                                        </span>
                                                        {isMobile ? (
                                                            <button
                                                                className="button pricing__button"
                                                                data-size="md"
                                                                data-variant="primary"
                                                                onClick={
                                                                    description.name !== 'Enterprise'
                                                                        ? getAction(description.name)
                                                                        : void 0
                                                                }
                                                            >
                                                                {description.name === 'Enterprise' ? (
                                                                    <a
                                                                        style={{ textDecoration: 'none' }}
                                                                        href="/contacts"
                                                                    >
                                                                        {t('Contact us')}
                                                                    </a>
                                                                ) : description.price ? (
                                                                    t('_common.subscribe')
                                                                ) : (
                                                                    t('sign up free')
                                                                )}
                                                            </button>
                                                        ) : null}
                                                    </>
                                                ) : (
                                                    <LetsTalk
                                                        title={description.name}
                                                        style={{ marginTop: '36px', marginBottom: '47px' }}
                                                    />
                                                )}
                                                <ColumnOptions
                                                    className="pricing__column-option"
                                                    options={options}
                                                    lang={lang}
                                                    shortened={isMobile}
                                                />
                                                {isMobile ? null : (
                                                    <div className="pricing__button-box">
                                                        <button
                                                            className="button pricing__button"
                                                            data-size="lg"
                                                            data-variant="primary"
                                                            onClick={
                                                                description.name !== 'Enterprise'
                                                                    ? getAction(description.name)
                                                                    : void 0
                                                            }
                                                        >
                                                            {description.name === 'Enterprise' ? (
                                                                <a style={{ textDecoration: 'none' }} href="/contacts">
                                                                    {t('Contact us')}
                                                                </a>
                                                            ) : description.price ? (
                                                                t('_common.subscribe')
                                                            ) : (
                                                                t('sign up free')
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )
                                    }}
                                </Column>
                            )
                        })}
                    </Table>
                </div>

                {isEducation && (
                    <DataBox
                        title={t('Schools and educational institutions')}
                        text={t(
                            'Interacty offers flexible solutions for schools and educational institutions. Contact us to find out more.',
                        )}
                        linkText={t("Let's talk!")}
                        linkUrl={'/auth'}
                        colorTheme="white"
                    />
                )}

                <h2>
                    {t('Need something special?')} <a href="/contacts">{t('Contact us')}</a>
                </h2>
            </div>
            <div className="QA">
                <h1>{t('Questions \nand Answer')}</h1>
                <QAChat data={QA_DATA} className="QA__chat" />
                <h2>
                    {t('Still have questions?\n')}
                    <a href="/contacts">{t('Contact us')}!</a>
                </h2>
            </div>
        </div>
    )
}

const PricingWrapper = ({ t, location }) => {
    const { is_auth } = useSelector(state => state)
    const path = location.pathname.split('/pricing/')[1]

    if (is_auth) return <Redirect to={{ pathname: 'account/billing-and-payments', search: '?tab=subscription' }} />
    else return <Pricing t={t} isEducation={path === 'education'} />
}

export default withTranslation('translations')(PricingWrapper)
